import { DialogComponent } from "@syncfusion/ej2-react-popups";
import { forwardRef, useImperativeHandle, useState } from "react";
import {
  completeOperation,
  completeProductionOrder,
  deleteOperation,
  executeWorkflow,
  getCheckList,
  getDocuments,
  getJobNote,
  getStartedJobByUser,
  holdProductionOrder,
  printTemplate,
  printTemplateAsPdf,
  reworkOperation,
  sendEmail,
  startOperation,
  stopOperation,
  unholdProductionOrder,
  updateJobNote,
} from "../../services/ApiService";
import { useAuth } from "../../utility/useAuth";
import { IJob } from "../../types/IJob";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { useStoreActions, useStoreState } from "../../store";
import { StopJobModal } from "../Modals/StopJobModal/StopJobModal";
import { DeleteJobModal } from "../Modals/DeleteJobModal/DeleteJobModal";
import dayjs from "dayjs";
import { AddNotesModal } from "../Modals/AddNotesModal/AddNotesModal";
import { StartJobModal } from "../Modals/StartJobModal/StartJobModal";
import { ReworkJobModal } from "../Modals/ReworkJobModal/ReworkJobModal";
import { getStartedOperation } from "../../utility/jobHelper";
import { CompleteProductionOrderModal } from "../Modals/CompleteProductionOrderModal/CompleteProductionOrderModal";
import { ErrorModal } from "../Modals/ErrorModal/ErrorModal";
import { StartNonProductionJobModal } from "../Modals/StartNonProductionJobModal/StartNonProductionJobModal";
import { CompleteJobModal } from "../Modals/CompleteJobModal/CompleteJobModal";
import { ICompleteJobRequest } from "../../types/ICompleteJobRequest";
import { InspectionLog } from "../../types/InspectionLog";
import { InspectionStatus } from "../../types/InspectionStatus";
import { DisplayCheckListModal } from "../Modals/DisplayCheckListModal/DisplayCheckListModal";
import { CheckListItem } from "../../types/CheckListItem";
import { ParametersModal } from "../Modals/ParametersModal/ParametersModal";
import { Parameter } from "../../types/Parameter";
import { WorkflowRequest } from "../../types/WorkflowRequest";
import { WorkflowResponse } from "../../types/WorkflowResponse";
import { PrintLabelModal } from "../Modals/PrintLabelModal/PrintLabelModal";
import { PathItem } from "../../types/PathItem";
import { PrintRequest } from "../../types/PrintRequest";
import { replaceExtension } from "../../utility/pathHelper";
import { InspectionToolSet } from "../../types/IInspectionToolSet";
import AsyncModal from "../Modals/AsyncModal/AsyncModal";
import { useNavigate } from "react-router";
import { DisplayCheckListModalPanel } from "../DisplayCheckListPanel/DisplayCheckListPanel";
import { ParametersContainer } from "../ParametersContainer/ParametersContainer";
import { EmailMessage } from "../../types/EmailMessage";
import { shortDateFormat, toUTC } from "../../utility/dateHelpers";
import { HoldProductionOrderModal } from "../Modals/HoldProductionOrderModal/HoldProductionOrderModal";

export interface IJobAction {
  startJob(job: IJob | undefined, isNonProduction: boolean): void;
  stopJob(job: IJob): void;
  reworkJob(job: IJob): void;
  completeJob(job: IJob): void;
  completeProductionOrder(job: IJob): void;
  holdProductionOrder(job: IJob): void;
  unholdProductionOrder(job: IJob): void;
  addJobNote(job: IJob): void;
  printLabel(job: IJob): void;
}

export const JobAction = forwardRef<IJobAction, {}>((props, ref) => {
  const auth = useAuth();
  const user = useStoreState((state) => state.user);
  const navigate = useNavigate();

  const { appSettings: actions } = useStoreActions((actions) => actions);

  useImperativeHandle(ref, () => ({
    async startJob(job, isNonProduction) {
      try {
        setJob(job);
        const startedJob = await getStartedJobByUser(auth);
        if (startedJob) {
          setJobNumber(startedJob.jobNumber);
          setOperation(getStartedOperation(startedJob) ?? startedJob.operation);
          setStopOperationDialogVisibility(true);
        } else {
          if (isNonProduction) {
            setShowNonProdStartJob(true);
          } else {
            setJobNumber(job?.jobNumber ?? "");
            setShowStartJob(true);
          }
        }
      } catch (error) {
        const message = `Failed to start job. Error: ${error}`;
        console.error(`Failed to start job. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      }
    },
    async stopJob(job) {
      try {
        if (job.openLoggedJobs.length === 0) {
          const message = `No started job`;
          console.error(message);
          ToastUtility.show(message, "Error", 2000);
          return;
        }
        setJob(job);
        setJobNumber(job.jobNumber);
        setOperation(getStartedOperation(job) ?? "");
        const secondsLast = dayjs().diff(
          dayjs(new Date(job.openLoggedJobs[0].loginDate)),
          's',
          true
        );
        console.log("secondsLast", secondsLast);

        if (secondsLast < 30) {
          setShowDeleteModal(true);
        } else {
          setShowStopModal(true);
        }
      } catch (error) {
        const message = `Failed to stop job. Error: ${error}`;
        console.error(`Failed to stop job. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      }
    },
    async reworkJob(job) {
      try {
        setJob(job);
        setJobNumber(job.jobNumber);
        setShowReworkModal(true);
      } catch (error) {
        const message = `Failed to rework job. Error: ${error}`;
        console.error(`Failed to rework job. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      }
    },
    async completeJob(job) {
      try {
        setJob(job);
        setJobNumber(job.jobNumber);
        setShowCompleteModal(true);
      } catch (error) {
        const message = `Failed to complete job. Error: ${error}`;
        console.error(`Failed to complete job. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      }
    },
    async completeProductionOrder(job) {
      try {
        const hasImages = await doesProductionOrderHaveImages(job.jobNumber);
        if (!hasImages) {
          const confirmationResult = await AsyncModal.open<boolean>({
            onHide: () => console.log("On hide"),
            okButtonName: "Yes",
            cancelButtonName: "No",
            cancelValue: false,
            confirmValue: true,
            children: (
              <>
                {`Completion production order requires at least one image.`}
                <br />
                {`Do you want to add an image now?`}
              </>
            ),
          });

          if (confirmationResult) {
            navigate(`/job-images/${job.jobNumber}`);
          }

          return;
        }
        setJob(job);
        setJobNumber(job.jobNumber);
        setShowCompletePOModal(true);
      } catch (error) {
        const message = `Failed to complete production order. Error: ${error}`;
        console.error(message);
        ToastUtility.show(message, "Error", 2000);
      }
    },
    async addJobNote(job) {
      try {
        setJob(job);
        const jobNotes = await getJobNote(auth, job.jobNumber);
        setJobNumber(job.jobNumber);
        setJobNotes(jobNotes);
        setShowAddNotesModal(true);
      } catch (error) {
        const message = `Failed to add job note. Error: ${error}`;
        console.error(`Failed to add job note. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      }
    },
    async holdProductionOrder(job) {
      try {
        setJob(job);
        setJobNumber(job.jobNumber);
        setShowHoldPOModal(true);
      } catch (error) {
        const message = `Failed to hold production order. Error: ${error}`;
        console.error(`Failed to hold production order. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      }
    },
    async unholdProductionOrder(job) {
      try {
        await unholdProductionOrder(auth, job.jobNumber);
      } catch (error) {
        const message = `Failed to unhold production order. Error: ${error}`;
        console.error(`Failed to unhold production order. Error: ${error}`);
        ToastUtility.show(message, "Error", 2000);
      }
    },
    async printLabel(job) {
      try {
        setJob(job);
        setJobNumber(job.jobNumber);
        setShowPrintLabelModal(true);
      } catch (error) {
        const message = `Failed to print label. Error: ${error}`;
        console.error(message);
        ToastUtility.show(message, "Error", 2000);
      }
    },
  }));

  const dialogHeader = "CSM Shopfloor";
  const [job, setJob] = useState<IJob>();
  const [jobNumber, setJobNumber] = useState("");
  const [operation, setOperation] = useState("");
  const [showStartJob, setShowStartJob] = useState(false);
  const [showNonProdStartJob, setShowNonProdStartJob] = useState(false);
  const [visibilityStopOperation, setStopOperationDialogVisibility] =
    useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showReworkModal, setShowReworkModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showAddNotesModal, setShowAddNotesModal] = useState(false);
  const [showCompletePOModal, setShowCompletePOModal] = useState(false);
  const [showHoldPOModal, setShowHoldPOModal] = useState(false);
  const [showCheckListModal, setShowCheckListModal] = useState(false);
  const [showParametersModal, setShowParametersModal] = useState(false);
  const [showPrintLabelModal, setShowPrintLabelModal] = useState(false);
  const [checkListItems, setCheckListItems] = useState<CheckListItem[]>([]);
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [jobNotes, setJobNotes] = useState("");

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("");

  async function validateOperationAction(
    jobNumber: string,
    operation: string,
    action: string,
    onOperationAction: () => void,
  ): Promise<void> {
    let request: WorkflowRequest = {};
    var response: WorkflowResponse;

    let shouldCancelAction = false;

    do {
      response = await executeWorkflow(
        auth,
        jobNumber,
        operation,
        action,
        request,
      );

      switch (response.customActionName) {
        case "EnterAdditionalInformation": {
          const params: Parameter[] = response.actionContext
            .parameters as Parameter[];
          params.map((param) => {
            if (param.type.startsWith("System.Int32")) {
              param.type = "number";
            } else if (param.type.startsWith("System.String")) {
              param.type = "string";
            } else if (param.type.startsWith("System.DateTime")) {
              param.type = "dateTime";
            } else if (param.type.startsWith("System.Boolean")) {
              param.type = "boolean";
            }
          });
          console.log(`${JSON.stringify(params)}`);

          const confirmationResult = await AsyncModal.open<Parameter[]>({
            onHide: () => console.log("On hide"),
            cancelValue: [],
            confirmValue: [],
            children: (
              <ParametersContainer
                jobNumber={jobNumber}
                title={"Please Enter Additional Information"}
                parameters={params}
              />
            ),
          });
          if (confirmationResult && confirmationResult.length) {
            console.log(
              `Return parameter: ${JSON.stringify(confirmationResult)}`,
            );

            const notes = confirmationResult
              .map((x) => `Set parameter ${x.displayName} with ${x.value}`)
              .join("\n");

            await updateJobNotes(jobNumber, notes);

            request.variables = confirmationResult.reduce(
              (a, v) => ({ ...a, [v.name]: v.value as object }),
              {},
            ) as {
              [key: string]: object;
            }[];
          }
          request.id = response.id;
          break;
        }
        case "DisplayCheckList": {
          const checkListName = response.actionContext.checkList as string;
          const checkList = await getCheckList(auth, checkListName);
          const checkListItems = checkList.items.map(
            (i) => ({ description: i }) as CheckListItem,
          );
          const confirmationResult = await AsyncModal.open<CheckListItem[]>({
            onHide: () => console.log("On hide"),
            cancelValue: [],
            confirmValue: [],
            children: (
              <DisplayCheckListModalPanel
                jobNumber={jobNumber}
                checkListName={checkList.name}
                checkListItems={checkListItems}
              />
            ),
          });
          if (confirmationResult && confirmationResult.length) {
            const notes = confirmationResult
              .map(
                (x) =>
                  `CheckList item ${x.description} set ${
                    x.checked === true ? "Checked" : "N/A"
                  }`,
              )
              .join("\n");

            await updateJobNotes(jobNumber, notes);
          } else {
            shouldCancelAction = true;
          }

          request.id = response.id;

          break;
        }
        case "SendEmail": {
          let email: EmailMessage = {
            subject: response.actionContext.subject,
            body: response.actionContext.message,
            to: [{ address: response.actionContext.email }],
          };
          await sendMail(email);
          break;
        }
        case "DisplayMessage": {
          const message = response.actionContext.message as string;
          await AsyncModal.open<boolean>({
            onHide: () => console.log("On hide"),
            cancelValue: false,
            confirmValue: true,
            okButtonName: "OK",
            hideCancelButton: true,
            children: <>{message}</>,
          });
          break;
        }
      }
      shouldCancelAction =
        shouldCancelAction ||
        ((response.actionContext?.cancelAction as boolean | undefined) ??
          false);
    } while (response && !response.isLastRule && !shouldCancelAction);

    if (!shouldCancelAction) {
      onOperationAction();
    }
  }

  async function doesProductionOrderHaveImages(
    jobNumber: string,
  ): Promise<boolean> {
    try {
      const documents = await getDocuments(auth, "images");
      return documents.some((d) => d.startsWith(`images${jobNumber}`));
    } catch (error) {
      handleError(error, "Failed to get images");
    }
    return false;
  }

  async function updateJobNotes(
    jobNumber: string,
    note: string,
  ): Promise<boolean> {
    try {
      let jobNotes = await getJobNote(auth, jobNumber);
      jobNotes += note;
      return await updateJobNote(auth, jobNumber, jobNotes);
    } catch (error) {
      handleError(error, "Failed to update job notes");
    }
    return false;
  }

  async function sendMail(email: EmailMessage): Promise<boolean> {
    try {
      await sendEmail(auth, email);
      return true;
    } catch (error) {
      handleError(error, "Failed to send email");
    }
    return false;
  }

  function onStartOperation(
    jobNumber: string,
    operation: string,
    from?: Date,
    to?: Date,
  ) {
    if (showNonProdStartJob) {
      setShowNonProdStartJob(false);
    } else {
      setShowStartJob(false);
    }
    console.log(`from ${from}`);
    const request = {
      from: from,
      to: to,
    };

    // startOperation(auth, jobNumber, operation, request)
    //   .then(() => {
    //     getStartedJobByUser(auth).then((job) => {
    //       actions.setStartedJob(job);
    //     });
    //   })
    //   .catch((error) => {
    //     handleError(error, "Failed to start operation");
    //   });

    validateOperationAction(jobNumber, operation, "start", () => {
      startOperation(auth, jobNumber, operation, request)
        .then(() => {
          getStartedJobByUser(auth).then((job) => {
            actions.setStartedJob(job);
          });
        })
        .catch((error) => {
          handleError(error, "Failed to start operation");
        });
    });
  }

  function onDeleteOperation(jobNumber: string, operation: string) {
    deleteOperation(auth, jobNumber, operation)
      .then(() => {
        setShowDeleteModal(false);
        getStartedJobByUser(auth).then((job) => {
          actions.setStartedJob(job);
        });
      })
      .catch((error) => {
        handleError(error, "Failed to delete operation.");
      });
  }

  function onReworkOperation(jobNumber: string, operation: string) {
    if (!operation || operation.length === 0) {
      setShowReworkModal(false);
      ToastUtility.show("No operation to rework", "Error", 2000);
      return;
    }
    reworkOperation(auth, jobNumber, operation)
      .then(() => {
        setShowReworkModal(false);
        getStartedJobByUser(auth).then((job) => {
          actions.setStartedJob(job);
        });
      })
      .catch((error) => {
        handleError(error, "Failed to rework operation.");
      });
  }

  function onCompleteProductionOrder(jobNumber: string) {
    setShowCompletePOModal(false);

    completeProductionOrder(auth, jobNumber)
      .then((job) => {
        actions.setStartedJob(job);
      })
      .catch((error) => {
        handleError(error, "Failed to complete production order.");
      });
  }

  function onCompleteJob(
    jobNumber: string,
    operation: string,
    inspectionToolSet: InspectionToolSet,
    reason: string,
    operationStatus: string,
    inspectionLogs: InspectionLog[],
  ) {
    setShowCompleteModal(false);
    const request: ICompleteJobRequest = {
      operationStatus: operationStatus,
      station: inspectionToolSet.name,
      inspectionToolSetId: inspectionToolSet.id,
      reason: reason,
      inspectionLogs: inspectionLogs.map((log) => {
        if (log.status.length === 0) {
          log.status = log.isAccepted
            ? InspectionStatus[InspectionStatus.Accepted]
            : InspectionStatus[InspectionStatus.Rejected];
        }
        return log;
      }),
    };

    validateOperationAction(jobNumber, operation, "complete", () => {
      completeOperation(auth, jobNumber, operation, request).catch((error) => {
        handleError(error, "Failed to complete operation");
      });
    });
    // completeOperation(auth, jobNumber, operation, request).catch((error) => {
    //   handleError(error, "Failed to complete operation");
    // });
  }

  function onStopOperation(jobNumber: string, operation: string) {
    validateOperationAction(jobNumber, operation, "stop", () => {
      stopOperation(auth, jobNumber, operation)
        .then(() => {
          setShowStopModal(false);
          getStartedJobByUser(auth).then((job) => {
            actions.setStartedJob(job);
          });
        })
        .catch((error) => {
          handleError(error, "Failed to stop operation.");
        });
    });
    // stopOperation(auth, jobNumber, operation)
    //   .then(() => {
    //     setShowStopModal(false);
    //     getStartedJobByUser(auth).then((job) => {
    //       actions.setStartedJob(job);
    //     });
    //   })
    //   .catch((error) => {
    //     handleError(error, "Failed to stop operation.");
    //   });
  }

  function onUpdateJobNote(jobNumber: string, jobNote: string) {
    setShowAddNotesModal(false);
    updateJobNote(auth, jobNumber, jobNote).catch((error) => {
      const message = `Failed to add job note. Error: ${error}`;
      console.error(message);
      ToastUtility.show(message, "Error", 2000);
    });
  }

  function onHoldPO(jobNumber: string, reason: string): void {
    setShowHoldPOModal(false);
    holdProductionOrder(auth, jobNumber, reason).catch((error) => {
      const message = `Failed to hold production order. Error: ${error}`;
      console.error(message);
      ToastUtility.show(message, "Error", 2000);
    });
  }

  function onUpdateParameters(jobNumber: string, paramaters: Parameter[]) {
    setShowParametersModal(false);
    const notes = paramaters
      .map((item) => {
        return `Set parameter ${item.name} with ${item.value}`;
      })
      .join("\\n");
    updateJobNote(auth, jobNumber, notes).catch((error) => {
      const message = `Failed to add job note. Error: ${error}`;
      console.error(message);
      ToastUtility.show(message, "Error", 2000);
    });
  }

  function onPrintLabel(
    template: PathItem,
    parameters: Parameter[],
    printAsPfd: boolean,
    printerLocation: string,
    printerName: string,
  ) {
    setShowPrintLabelModal(false);
    const substr: {
      [key: string]: string;
    } = {};

    parameters.forEach(
      (p, index) =>
        (substr[p.name] =
          p.type === "dateTime"
            ? shortDateFormat(p?.value?.toString() ?? "")
            : (p?.value?.toString() ?? "")),
    );

    const request: PrintRequest = {
      templateName: `${template.folder}\\${template.fileName}`,
      substrings: substr,
      noLabels:
        (parameters.find((p) => p.name === "NoLabels")?.value as number) ?? 1,
      printerName: printerName,
      location: printerLocation,
    };

    if (printAsPfd) {
      printTemplateAsPdf(auth, request)
        .then((data) => {
          if (data) {
            const href = URL.createObjectURL(data);
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute(
              "download",
              replaceExtension(template.fileName, "pdf"),
            ); //or any other extension
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
          }
        })
        .catch((error) => {
          handleError(error, "Failed to print as PDF label operation.");
        });
    } else {
      printTemplate(auth, request)
        .then((data) => {
          ToastUtility.show("The print job sent to the spooler.", "Info", 2000);
        })
        .catch((error) => {
          handleError(error, "Failed to print label operation.");
        });
    }
  }

  function blobToText(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const buffer = reader.result as ArrayBuffer;
        const decoder = new TextDecoder();
        const text = decoder.decode(buffer);
        resolve(text);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }

  function handleError(error: any, prefix?: string) {
    if (error?.response && error?.response?.data) {
      blobToText(error.response.data).then((text) => {
        setErrorMessage(text);
        setShowErrorModal(true);
        return;
      }).catch((err) => {
        const text = error.response.data.toString();
        setErrorMessage(text);
        setShowErrorModal(true);
        return;
      });
    }
    const message = `${prefix} Error: ${error}`;
    console.error(message);
    ToastUtility.show(message, "Error", 2000);
  }

  const buttons = [
    {
      buttonModel: {
        content: "Yes",
        cssClass: "e-flat",
        isPrimary: true,
      },
      click: () => {
        setStopOperationDialogVisibility(false);
        stopOperation(auth, jobNumber, operation).then(() => {
          if (job) {
            setJobNumber(job.jobNumber);
            setShowStartJob(true);
          } else {
            setShowNonProdStartJob(true);
          }
          getStartedJobByUser(auth).then((job) => {
            actions.setStartedJob(job);
          });
        });
      },
    },
    {
      buttonModel: {
        content: "No",
        cssClass: "e-flat",
      },
      click: () => {
        setStopOperationDialogVisibility(false);
      },
    },
  ];

  function dialogClose() {
    setStopOperationDialogVisibility(false);
  }

  const firstPosition = { X: 400, Y: 200 };

  return (
    <div id="dialog-target">
      {showErrorModal ? (
        <ErrorModal
          open={showErrorModal}
          error={errorMessage}
          title={errorTitle}
          onClose={() => {
            setShowErrorModal(false);
          }}
        />
      ) : (
        <></>
      )}
      {showCompleteModal ? (
        <CompleteJobModal
          open={showCompleteModal}
          job={job}
          user={user.info}
          onClose={() => {
            setShowCompleteModal(false);
          }}
          onCompleteJob={onCompleteJob}
        />
      ) : (
        <></>
      )}
      {showCompletePOModal ? (
        <CompleteProductionOrderModal
          open={showCompletePOModal}
          jobNumber={jobNumber}
          user={user.info.name}
          onClose={() => {
            setShowCompletePOModal(false);
          }}
          onCompleteProductionOrder={onCompleteProductionOrder}
        />
      ) : (
        <></>
      )}
      {showReworkModal ? (
        <ReworkJobModal
          open={showReworkModal}
          jobNumber={jobNumber}
          user={user.info.name}
          isRejected={job?.operation === "QUAR"}
          onClose={() => {
            setShowReworkModal(false);
          }}
          onReworkJob={onReworkOperation}
        />
      ) : (
        <></>
      )}
      {showAddNotesModal ? (
        <AddNotesModal
          open={showAddNotesModal}
          jobNumber={jobNumber}
          note={jobNotes}
          onAddNote={onUpdateJobNote}
          onClose={() => {
            setShowAddNotesModal(false);
          }}
        />
      ) : (
        <></>
      )}
      {showHoldPOModal ? (
        <HoldProductionOrderModal
          open={showHoldPOModal}
          jobNumber={jobNumber}
          onHoldProductionOrder={onHoldPO}
          onClose={() => {
            setShowHoldPOModal(false);
          }}
        />
      ) : (
        <></>
      )}
      {showDeleteModal ? (
        <DeleteJobModal
          open={showDeleteModal}
          jobNumber={jobNumber}
          operation={operation}
          onDeleteJob={onDeleteOperation}
          onClose={() => {
            setShowDeleteModal(false);
            setShowStopModal(true);
          }}
        />
      ) : (
        <></>
      )}
      {showStopModal ? (
        <StopJobModal
          open={showStopModal}
          onClose={() => {
            setShowStopModal(false);
          }}
          user={user.info.name}
          jobNumber={jobNumber}
          operation={operation}
          onStopJob={onStopOperation}
        />
      ) : (
        <></>
      )}
      {showCheckListModal ? (
        <DisplayCheckListModal
          open={showCheckListModal}
          onClose={() => {
            setShowCheckListModal(false);
          }}
          checkListName={user.info.name}
          jobNumber={jobNumber}
          checkListItems={checkListItems}
          onUpdateNote={onUpdateJobNote}
        />
      ) : (
        <></>
      )}

      {showParametersModal ? (
        <ParametersModal
          open={showParametersModal}
          onClose={() => {
            setShowParametersModal(false);
          }}
          title={"Please Enter Additional Information"}
          jobNumber={jobNumber}
          parameters={parameters}
          onUpdateParameters={onUpdateParameters}
        />
      ) : (
        <></>
      )}

      {showPrintLabelModal ? (
        <PrintLabelModal
          open={showPrintLabelModal}
          onClose={() => {
            setShowPrintLabelModal(false);
          }}
          jobNumber={jobNumber}
          onPrint={onPrintLabel}
        />
      ) : (
        <></>
      )}

      {visibilityStopOperation ? (
        <DialogComponent
          width="250px"
          close={dialogClose}
          header={dialogHeader}
          visible={visibilityStopOperation}
          showCloseIcon={true}
          buttons={buttons}
          position={firstPosition}
        >
          You are already logged into {jobNumber} job and {operation} operation,
          do you want to stop this operation before you can start new one?
        </DialogComponent>
      ) : (
        <></>
      )}
      {showStartJob ? (
        <StartJobModal
          open={showStartJob}
          jobNumber={jobNumber}
          jobRouting={job?.routingNo ?? 0}
          user={user.info.name}
          onClose={() => {
            setShowStartJob(false);
          }}
          onStartJob={onStartOperation}
        />
      ) : (
        <></>
      )}
      {showNonProdStartJob ? (
        <StartNonProductionJobModal
          open={showNonProdStartJob}
          user={user.info.name}
          onClose={() => {
            setShowNonProdStartJob(false);
          }}
          onStartJob={onStartOperation}
        />
      ) : (
        <></>
      )}
    </div>
  );
});
